import { Info } from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar } from 'recharts';
import { formatCurrency } from '../../../../../utils/CurrencyUtils';
import { formatDate } from '../../../../../utils/DateUtils';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { isPastDate, isWithinSevenDays } from './Loans';
import {
  LoanDetails,
  LoanType,
  FuelAdvanceStatus,
} from '../../../../../types/apiTypes';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import useApi from '../../../../../utils/AxiosWrapper';
import PayTowardAdvanceCTA from './PayTowardAdvanceCTA';

interface LoanDetailsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  addClass?: string;
  selectedLoan: any | null;
}

function LoanDetailsPage({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  addClass,
}: LoanDetailsProps) {
  const { screenView } = useScreenView();
  const { loan_id } = useParams();
  const { callProtectedApi } = useApi();
  const { data: loanDetails = null, isLoading } = useQuery<LoanDetails>({
    queryKey: ['loans', loan_id ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/credit/loans/${loan_id}`,
        'get',
        {}
      );
      const loan = response?.data?.data?.loan || [];
      loan.paid = loan.total_cost_of_loan - loan.current_balance;
      return loan;
    },
    enabled: !!loan_id,
  });

  const isFuelAdvance = loanDetails?.type === LoanType.FUEL_ADVANCE;

  const getFuelAdvanceStatusBadge = (status?: string | null) => {
    if (!status) return null;
    const statusConfig: Record<
      string,
      { label: string; color: string; bg: string }
    > = {
      [FuelAdvanceStatus.PENDING_REVIEW]: {
        label: 'Pending Review',
        color: 'text-yellow-700',
        bg: 'bg-yellow-100',
      },
      [FuelAdvanceStatus.APPROVED]: {
        label: 'Approved',
        color: 'text-green-700',
        bg: 'bg-green-100',
      },
      [FuelAdvanceStatus.FUNDED]: {
        label: 'Funded',
        color: 'text-green-700',
        bg: 'bg-green-100',
      },
      [FuelAdvanceStatus.ACTIVE]: {
        label: 'Active',
        color: 'text-blue-700',
        bg: 'bg-blue-100',
      },
      [FuelAdvanceStatus.REPAID]: {
        label: 'Repaid',
        color: 'text-green-700',
        bg: 'bg-green-100',
      },
      [FuelAdvanceStatus.REJECTED]: {
        label: 'Rejected',
        color: 'text-red-700',
        bg: 'bg-red-100',
      },
      [FuelAdvanceStatus.PAST_DUE]: {
        label: 'Past Due',
        color: 'text-orange-700',
        bg: 'bg-orange-100',
      },
      [FuelAdvanceStatus.DEFAULTED]: {
        label: 'Defaulted',
        color: 'text-red-700',
        bg: 'bg-red-100',
      },
    };
    const config = statusConfig[status] || {
      label: status,
      color: 'text-gray-700',
      bg: 'bg-gray-100',
    };
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.color} ${config.bg}`}
      >
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <ClipLoader
        color="#1D213B"
        loading={true}
        size={60}
        aria-label="Loading Spinner"
        data-testid="loader"
        className="mt-10 flex h-full w-full justify-center"
      />
    );
  }

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`flex h-full flex-col items-center justify-center space-y-[16px] overflow-y-auto overflow-x-hidden pt-[16px] sm:w-[95%] md:w-full md:px-4`}
          >
            <PayTowardAdvanceCTA loan={loanDetails} />
            <div className="flex h-full w-full flex-col gap-6 overflow-x-hidden rounded-[8px] border-[1px] border-bordergray bg-white p-4 lg:w-[760px] lg:max-w-[760px]">
              <div className="flex flex-col justify-between gap-4 rounded-[8px] border-[1px] border-bordergray p-4">
                <div className="flex w-full flex-col justify-start">
                  <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                    <div className="flex flex-row items-center space-x-1">
                      <p className="text-[18px] font-[700] text-graytertiary">
                        Paid:{' '}
                      </p>
                      {loanDetails?.total_cost_of_loan != null &&
                        loanDetails?.current_balance != null && (
                          <h2 className="text-[20px] font-[700] text-primary">
                            {formatCurrency(
                              loanDetails?.total_cost_of_loan -
                                loanDetails?.current_balance
                            )}
                          </h2>
                        )}
                    </div>
                    <div className="flex flex-row items-center space-x-1">
                      <p className="text-[18px] font-[700] text-graytertiary">
                        Left:{' '}
                      </p>
                      {loanDetails?.current_balance != null && (
                        <h2 className="text-[20px] font-[700] text-primary">
                          {formatCurrency(loanDetails?.current_balance)}
                        </h2>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between space-x-4">
                    <div className="h-6 w-[577px] max-w-[577px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[loanDetails]} layout="vertical">
                          {/* <CartesianGrid strokeDasharray="3 3" /> */}
                          <XAxis type="number" domain={[0, 100]} hide />
                          <YAxis type="category" dataKey="name" hide />
                          {/*<Legend
              // content={() => renderCustomLegend(normalizedData[0])}
              verticalAlign="bottom"
              align="left"
                />*/}
                          {loanDetails?.end_date &&
                            loanDetails?.current_balance &&
                            loanDetails?.total_cost_of_loan &&
                            loanDetails?.current_balance !== 0 &&
                            loanDetails?.total_cost_of_loan -
                              loanDetails?.current_balance !==
                              0 && (
                              <>
                                <Bar
                                  dataKey="paid"
                                  fill={
                                    isPastDate(loanDetails?.end_date)
                                      ? '#C64949'
                                      : isWithinSevenDays(loanDetails?.end_date)
                                        ? '#E2660C'
                                        : `#1D213B`
                                  }
                                  stackId="a"
                                  radius={[10, 0, 0, 10]}
                                />
                                <Bar
                                  dataKey="current_balance"
                                  fill="#E6E6E6"
                                  stackId="a"
                                  radius={[0, 10, 10, 0]}
                                />
                              </>
                            )}
                          {loanDetails?.current_balance === 0 && (
                            <Bar
                              dataKey="paid"
                              fill="#6A9E68"
                              stackId="a"
                              radius={[10, 10, 10, 10]}
                            />
                          )}
                          {loanDetails?.current_balance &&
                            loanDetails?.total_cost_of_loan &&
                            loanDetails?.total_cost_of_loan -
                              loanDetails?.current_balance ===
                              0 && (
                              <Bar
                                dataKey="current_balance"
                                fill="#E6E6E6"
                                stackId="a"
                                radius={[10, 10, 10, 10]}
                              />
                            )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {loanDetails?.total_cost_of_loan && (
                      <p className="text-[20px] font-[700] text-primary">
                        {formatCurrency(loanDetails?.total_cost_of_loan)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {loanDetails?.end_date &&
                loanDetails?.current_balance != null &&
                loanDetails?.current_balance > 0 &&
                isPastDate(loanDetails?.end_date) && (
                  <div className="flex w-full flex-row items-center justify-between rounded-[8px] border-[1px] border-[#C64949] bg-[#FBEEEE] p-3 align-middle">
                    <div className="flex flex-row items-center justify-start gap-x-2">
                      <Info size={24} className="text-[#C64949]" />{' '}
                      <p className="text-[14px] text-primary">
                        Your payment is past due. Please make the payment
                        immediately to avoid additional penalties and fees.
                      </p>
                    </div>
                  </div>
                )}
              {loanDetails?.end_date &&
                loanDetails?.current_balance != null &&
                loanDetails?.current_balance > 0 &&
                !isPastDate(loanDetails?.end_date) &&
                isWithinSevenDays(loanDetails?.end_date) && (
                  <div className="flex w-full flex-row items-center justify-between rounded-[8px] border-[1px] border-[#E2660C] bg-[#FCF0E7] p-3 align-middle">
                    <div className="flex flex-row items-center justify-start gap-x-2">
                      <Info size={24} className="text-[#E2660C]" />{' '}
                      <p className="text-[14px] text-primary">
                        Your payment is due soon. Please make the payment to
                        avoid any penalties or additional fees.
                      </p>
                    </div>
                  </div>
                )}
              <div className="grid w-full grid-cols-1 gap-4 md:h-[304px] md:max-h-[304px] md:grid-cols-2 md:grid-rows-4">
                <div className="col-span-1 row-span-5 flex flex-col gap-4 rounded-[8px] border-[1px] border-bordergray p-4 text-[16px] text-primary">
                  <h2 className="text-[18px] font-[600]">
                    {isFuelAdvance
                      ? 'Fuel Advance Information'
                      : 'General Cash Advance Information'}
                  </h2>
                  {isFuelAdvance ? (
                    <div className="grid w-full grid-cols-2 gap-2">
                      <p className="text-[14px] text-graysecondary">Status</p>
                      <div>
                        {getFuelAdvanceStatusBadge(
                          loanDetails?.loanRequest?.fuel_advance_status
                        )}
                      </div>
                      <p className="text-[14px] text-graysecondary">
                        Requested Amount
                      </p>
                      {loanDetails?.loanRequest?.amount && (
                        <p className="text-[16px]">
                          {formatCurrency(loanDetails?.loanRequest?.amount)}
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">
                        Amount Received
                      </p>
                      {loanDetails?.cash_to_customer && (
                        <p className="text-[16px]">
                          {formatCurrency(loanDetails?.cash_to_customer)}
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">
                        Fee for Services
                      </p>
                      {loanDetails?.fees_set_at_origination && (
                        <p className="text-[16px]">
                          {formatCurrency(loanDetails?.fees_set_at_origination)}
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">
                        Delivery Date
                      </p>
                      <p className="text-[16px]">
                        {loanDetails?.delivery_date
                          ? formatDate(new Date(loanDetails.delivery_date))
                          : '-'}
                      </p>
                      <p className="text-[14px] text-graysecondary">Due Date</p>
                      <p
                        className={`text-[16px] ${loanDetails?.end_date && isPastDate(loanDetails.end_date) ? 'font-[600] text-[#C64949]' : ''}`}
                      >
                        {loanDetails?.end_date
                          ? formatDate(new Date(loanDetails.end_date))
                          : '-'}
                      </p>
                      <p className="text-[14px] text-graysecondary">
                        Repayment
                      </p>
                      <p className="text-[16px]">
                        {loanDetails?.is_greedy_repayment
                          ? '100% of deposits'
                          : 'Standard'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid w-full grid-cols-2 gap-2">
                      <p className="text-[14px] text-graysecondary">
                        Requested Amount
                      </p>
                      {loanDetails?.loanRequest?.amount && (
                        <p className="text-[16px]">
                          {formatCurrency(loanDetails?.loanRequest?.amount)}
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">
                        Amount Received
                      </p>
                      {loanDetails?.cash_to_customer && (
                        <p className="text-[16px]">
                          {formatCurrency(loanDetails?.cash_to_customer)}
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">
                        Issuance date
                      </p>
                      <p className="text-[16px]">
                        {formatDate(
                          loanDetails?.originated_at
                            ? new Date(loanDetails?.originated_at)
                            : new Date()
                        )}
                      </p>
                      <p className="text-[14px] text-graysecondary">Term</p>
                      {loanDetails?.repayment_months && (
                        <p className="text-[16px]">
                          {loanDetails?.repayment_months} months
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">
                        Fee for Services
                      </p>
                      {loanDetails?.fees_set_at_origination && (
                        <p className="text-[16px]">
                          {formatCurrency(loanDetails?.fees_set_at_origination)}
                        </p>
                      )}
                      <p className="text-[14px] text-graysecondary">Due date</p>
                      <p className="text-[16px]">
                        {formatDate(
                          loanDetails?.end_date
                            ? new Date(loanDetails?.end_date)
                            : new Date()
                        )}
                      </p>
                    </div>
                  )}
                </div>
                <div className="col-span-1 row-span-4 flex flex-col gap-4 rounded-[8px] border-[1px] border-bordergray p-4 text-[16px] text-primary md:h-[304px] md:min-h-[304px]">
                  <h2 className="text-[18px] font-[600]">
                    Current Advance Status
                  </h2>
                  <div className="grid w-full grid-cols-2 gap-2">
                    <p className="text-[14px] text-graysecondary">
                      Total amount paid
                    </p>
                    {loanDetails?.current_balance != null &&
                      loanDetails?.total_cost_of_loan != null && (
                        <p className="text-[16px]">
                          {formatCurrency(
                            loanDetails?.total_cost_of_loan -
                              loanDetails?.current_balance
                          )}
                        </p>
                      )}
                    <p className="text-[14px] text-graysecondary">
                      Remaining balance
                    </p>
                    {loanDetails?.current_balance != null && (
                      <p className="text-[16px]">
                        {formatCurrency(loanDetails?.current_balance)}
                      </p>
                    )}
                    {!isFuelAdvance && (
                      <>
                        <p className="text-[14px] text-graysecondary">
                          % of income
                        </p>
                        <p className="text-[16px]">
                          {loanDetails?.percent_of_income_needed_for_payment}
                        </p>
                      </>
                    )}
                    {isFuelAdvance && loanDetails?.is_greedy_repayment && (
                      <p className="col-span-2 mt-2 text-[13px] text-graysecondary">
                        100% of incoming deposits are applied toward repayment
                        until the balance is paid in full.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={`flex h-full w-full flex-col items-start gap-x-[16px] align-middle ${addClass} gap-4 bg-white`}
              >
                <h2 className="text-[18px] font-[600]">Payment History</h2>
                <div className="h-full max-h-[500px] w-full overflow-x-hidden bg-white">
                  {loanDetails?.payments &&
                  loanDetails?.payments?.length > 0 ? (
                    <table className="h-full min-w-full table-auto overflow-y-auto rounded-[8px] bg-white">
                      <thead className="border-[1px] border-bordergray bg-primarybg">
                        <tr>
                          <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                            Date
                          </th>
                          <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                            Paid
                          </th>
                        </tr>
                      </thead>
                      <tbody className="border-bordergray bg-white">
                        {loanDetails?.payments?.map((payment, index) => (
                          <tr
                            key={index}
                            className="border-[1px] border-bordergray"
                          >
                            <td className="px-4 py-2 text-[14px] text-primary">
                              {formatDate(
                                new Date(payment?.created_at ?? ''),
                                true
                              )}
                            </td>
                            <td className="px-4 py-2 text-right text-[14px] text-primary">
                              {formatCurrency(payment?.amount ?? 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-[16px] text-primary">No payments yet</p>
                  )}
                </div>
              </div>
              <p className="w-full text-right text-[12px] text-graysecondary">
                ID#{loanDetails?.loan_id}
              </p>
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default LoanDetailsPage;
